import { canSeeSection } from '../utils/managerAccess'
import { useEffect, useState, type CSSProperties } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  saveTenantSettings,
  subscribeStaff,
  saveStaffDisplayName,
  saveStaffPerms,
  subscribeTables,
  subscribeHalls,
  saveHall,
  deleteHall,
  saveTable,
  deleteTable,
} from '../services/products'
import { createStaff, removeStaff, resetStaffCredentials } from '../services/staff'
import {
  ChevronLeftIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
  EyeOffIcon,
  PencilIcon,
  TrashIcon,
  StoreIcon,
  TabletIcon,
  HeartIcon,
  ReceiptTextIcon,
  BanknoteIcon,
  NotebookPenIcon,
  QrCodeIcon,
  ChefHatIcon,
  GiftIcon,
  UserIcon,
  PrinterIcon,
  ArrowRightIcon,
  TableIcon,
  KeyRoundIcon,
  ChevronRightIcon,
  XIcon,
  CheckIcon,
} from '../components/icons'
import { ReceiptPreview } from '../components/receipt/ReceiptPreview'
import { QrMenuPanel } from '../components/settings/QrMenuPanel'
import { AccountPanel, trialSummary } from '../components/settings/AccountPanel'
import { LockedNote } from '../components/LockedNote'
import { LoyaltyPanel } from '../components/settings/LoyaltyPanel'
import { useUpgrade } from '../context/UpgradeContext'
import { KitchenBellSetting } from '../components/settings/KitchenBellSetting'
import { KitchenScreensPanel } from '../components/settings/KitchenScreensPanel'
import { CampaignsPanel } from '../components/settings/CampaignsPanel'
import {
  DEFAULT_CASH_RECEIPT,
  DEFAULT_RECEIPT,
  PERMS,
  type AdminUser,
  type Perms,
  type PermKey,
  type ReceiptSettings,
  type Role,
  type Order,
  type Table,
  type Hall,
} from '../types'
import type { ReceiptKind } from '../components/receipt/Receipt'
import { ConfirmDialog, type ConfirmSpec } from '../components/pos/ConfirmDialog'
import { PromptDialog, type PromptSpec } from '../components/pos/PromptDialog'
import { BulkTablesDialog } from '../components/pos/BulkTablesDialog'
import { MoreMenu } from '../components/MoreMenu'
import { hallTone } from '../utils/hallTones'
import './SettingsPage.css'
import { Select } from '../components/Select'
import { useCatalog } from '../context/CatalogContext'
import { PrintersPanel } from '../components/settings/PrintersPanel'
import { useOpenTickets } from '../context/OpenTicketsContext'
import { stationNames } from '../utils/stations'
import { DeviceRows } from '../components/settings/DeviceRows'
import { PermChips } from '../components/settings/PermChips'
import { toDayKey } from '../utils/dayKey'
import { PrinterlessNote, UsePrinterSwitch } from '../components/settings/UsePrinterSwitch'
import { tableLabel } from '../utils/tableLabel'
import { hallColor } from '../utils/hallTones'

/**
 * The slip the test button prints. Fixed rather than a real order: this is for
 * checking how the paper comes out, rule weight, boxes, spacing, and it has
 * to be printable on a quiet morning when no order exists to borrow.
 */
const TEST_ORDER: Order = {
  id: 'deneme',
  receiptNo: 'DENEME',
  dayKey: '',
  deviceId: '',
  cashierUid: '',
  cashierEmail: '',
  tableName: 'Salon → Masa 1',
  openedByName: 'Deneme',
  lines: [
    {
      productId: 'a',
      name: 'Tavuk Döner',
      unitPriceKurus: 20000,
      qty: 2,
      lineTotalKurus: 40000,
      station: 'Mutfak',
    },
    {
      productId: 'b',
      name: 'Ayran',
      unitPriceKurus: 3000,
      qty: 2,
      lineTotalKurus: 6000,
      note: 'soğuk',
      // A second station on purpose: the slip splits in two, which is the only
      // case where the station name, and so the box around it, is printed at
      // all. A single-station test would print neither and look broken.
      station: 'İçecek',
    },
  ],
  itemCount: 4,
  totalKurus: 46000,
  paymentMethod: 'paid',
  status: 'completed',
  // Never written to Firestore, so there is no server stamp to carry.
  createdAt: null,
  createdAtLocal: Date.now(),
}

const ROLES: Role[] = ['owner', 'manager', 'cashier', 'waiter', 'kitchen']

/** The rule styles offered, each drawn as itself in the picker. */
const RULE_STYLES: [NonNullable<ReceiptSettings['ruleStyle']>, string][] = [
  ['solid', 'Düz'],
  ['dashed', 'Kesik'],
  ['dotted', 'Noktalı'],
  ['double', 'Çift'],
]

interface StaffDraft {
  displayName: string
  username: string
  pin: string
  role: Exclude<Role, 'owner' | 'kiosk'>
  /** Mutfak rolünde tezgahı; '' = bütün tezgahlar. */
  station: string
}

const emptyStaff: StaffDraft = { displayName: '', username: '', pin: '', role: 'cashier', station: '' }

/**
 * A settings section that folds away.
 *
 * The page had grown to five stacked blocks, and the receipt panel alone is
 * taller than a laptop screen, finding "Kullanıcılar" meant scrolling past all
 * of it. Everything starts closed so the page opens as a list of headings.
 *
 * `actions` sits in the header and stays visible while open, because on the
 * receipt panel that is where Kaydet lives and it must not be scrolled away.
 */
function Section({
  title,
  hint,
  actions,
  open,
  anyOpen,
  onToggle,
  standalone,
  icon,
  size = 'small',
  tone = 'brand',
  children,
}: {
  title: string
  hint?: string
  /** Kapalıyken kart olarak görünür (kullanıcı, 13 Eki 2026): simge ve boyut. */
  icon?: React.ReactNode
  size?: 'big' | 'wide' | 'small'
  tone?: 'brand' | 'blue' | 'green' | 'violet' | 'amber' | 'slate'
  /** Kendi sayfasında tek başına (Masalar): sayfa başlığı, kart yok. */
  standalone?: boolean
  actions?: React.ReactNode
  /** Whether this is the section currently expanded. Owned by the page. */
  open: boolean
  /**
   * Whether some section, this one or another, is expanded.
   *
   * A section that is not the open one steps aside entirely rather than
   * staying in the list as a closed bar: the expanded panel is what the
   * person came for, and five other headings around it are five other things
   * to read past. Closing brings the whole list back.
   */
  anyOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  if (anyOpen && !open) return null

  // Kendi sayfasında (Masalar): Menü Yönetimi gibi, başlık ve düğmeler
  // kartın dışında, sayfanın üstünde (kullanıcı, 6 Eki 2026).
  if (standalone) {
    return (
      <>
        <div className="set-page-head set-page-head--standalone">
          <h1>{title}</h1>
          {actions}
        </div>
        <div className="set-page-body">{children}</div>
      </>
    )
  }

  // Kapalı: ayarlar ana sayfasında bir kart (kare ya da dikdörtgen).
  if (!open && icon) {
    return (
      <button type="button" className={`set-card set-card--${size} set-card--${tone}`} onClick={onToggle}>
        <span className="set-card__icon">{icon}</span>
        <span className="set-card__text">
          <b>{title}</b>
          {hint && <small>{hint}</small>}
        </span>
        <span className="set-card__go" aria-hidden="true">
          <ArrowRightIcon />
        </span>
      </button>
    )
  }

  // Açık bölüm: Masa Yönetimi sayfası gibi, büyük başlık ve düğmeler kartın
  // dışında, solda geri simgesi (kullanıcı, 13 Eki 2026).
  if (open && icon) {
    return (
      <>
        <div className="set-page-head set-page-head--section">
          <div className="set-page-head__title">
            <button
              type="button"
              className="set-back"
              onClick={onToggle}
              aria-label="Tüm ayarlara dön"
              title="Tüm ayarlara dön"
            >
              <ChevronLeftIcon />
            </button>
            <div>
              <h1>{title}</h1>
              {hint && <p className="set-page-head__hint">{hint}</p>}
            </div>
          </div>
          {actions && <div className="set-page-head__actions">{actions}</div>}
        </div>
        <div className="set-page-body set-page-body--card">{children}</div>
      </>
    )
  }

  return (
    <section className={`set-block ${open ? 'is-open' : ''}`}>
      <div className="set-block__head">
        {/* The whole bar is the target, not just the chevron: a heading that
            looks pressable should be pressable anywhere along it. */}
        <h2 className="set-block__h">
          <button
            type="button"
            className="set-block__toggle"
            onClick={onToggle}
            aria-expanded={open}
            /* Open, it is the way back to the list rather than a way to
               collapse one item among many, the others are gone. */
            title={open ? 'Tüm ayarlara dön' : undefined}
          >
            {open ? <ChevronLeftIcon /> : <ChevronDownIcon />}
            <span className="set-block__title">{title}</span>
            {hint && <span className="set-block__hint">{hint}</span>}
          </button>
        </h2>
        {open && actions}
      </div>
      {/* Unmounted rather than hidden: the receipt preview is a live render and
          there is no reason to keep it drawing behind a closed panel. */}
      {open && <div className="set-block__body">{children}</div>}
    </section>
  )
}

interface Props {
  /** Sends a slip to the printer, for the test print under the preview. */
  onPrint: (order: Order, kind?: ReceiptKind) => void
  /**
   * Yalnız bu bölüm, kendi sayfasında (Masalar sol menüde Menü'nün altında,
   * kullanıcı 6 Eki 2026). Öteki bölümler gizli, başlık geri götürmüyor.
   */
  only?: string
}

export function SettingsPage({ onPrint, only }: Props) {
  /** Mutfak personeline tezgah seçtirmek için: kategorilerdeki tezgah adları. */
  const { categories, products } = useCatalog()
  /** Masa silinirken açık adisyon var mı diye. */
  const { openTickets } = useOpenTickets()
  const stationOptions = [
    { value: '', label: 'Bütün tezgahlar' },
    ...stationNames(categories, products).map((st) => ({ value: st, label: st })),
  ]
  const { tenantId, tenantName, roleLabels, receiptSettings, cashReceiptSettings, hasFeature, trialEndsAt, printers, usePrinter, staffLimit, plan, isOwner: viewerOwner, can } = useAuth()
  /** Müdür (3 Kas 2026): yalnız izni olan bölümleri görür; Hesabım yalnız patronun. */
  const see = (id: string) => canSeeSection(id, viewerOwner, can)
  const askUpgrade = useUpgrade()
  /**
   * The receipt being edited. Held apart from what is saved so the preview can
   * follow every keystroke without a write per character, a restaurant on a weak
   * signal does not need thirty writes to change one word.
   */
  const [receiptDraft, setReceiptDraft] = useState<ReceiptSettings | null>(null)
  /**
   * Hangi fiş düzenleniyor (16 Eki 2026): kasada ödeme alınınca çıkan kasa
   * fişi mi, garsonun girdiği siparişin fişi mi. İkisinin ayarı ayrı.
   */
  const [receiptTab, setReceiptTab] = useState<'cash' | 'station'>('cash')
  const [receiptBusy, setReceiptBusy] = useState(false)
  /**
   * Which section is expanded, one at a time, so the page stays a short list
   * of headings however long the panels are. Null means all closed.
   */
  /**
   * Which section is expanded, taken from the address so the sidebar can open
   * one directly, "/ayarlar?bolum=tables" lands on Masalar already open.
   *
   * In the URL rather than in state alone: the back button then walks back
   * through sections the way it walks back through pages, and a link to a
   * section is a link someone can send.
   */
  const [search, setSearch] = useSearchParams()
  const openSection = only ?? search.get('bolum')
  // Masalar artık kendi sayfasında; eski "?bolum=tables" bağlantıları oraya.
  // Kurum bölümü Hesabım'a katıldı; "?bolum=yurt" Hesabım'ı açıyor.
  // Sistem de Yazıcılar'a; "?bolum=system" Yazıcılar'ı açıyor.
  const navigate = useNavigate()
  useEffect(() => {
    if (!only && openSection === 'tables' && tenantId) navigate(`/${tenantId}/masalar`, { replace: true })
    if (!only && openSection === 'yurt' && tenantId) navigate(`/${tenantId}/ayarlar?bolum=hesap`, { replace: true })
    // Sistem bölümü Yazıcılar'a katıldı (20 Eki 2026).
    if (!only && openSection === 'system' && tenantId) navigate(`/${tenantId}/ayarlar?bolum=printers`, { replace: true })
  }, [only, openSection, tenantId, navigate])

  const setOpenSection = (next: string | null | ((cur: string | null) => string | null)) => {
    const value = typeof next === 'function' ? next(openSection) : next
    // replace: choosing sections is browsing one screen, not visiting six,
    // a history entry each would bury whatever came before Ayarlar.
    setSearch(value ? { bolum: value } : {}, { replace: true })
  }
  const [staff, setStaff] = useState<AdminUser[]>([])
  const [tables, setTables] = useState<Table[]>([])
  const [halls, setHalls] = useState<Hall[]>([])
  /**
   * Which section's tables are on screen. `null` is the "Salonsuz" tab,
   * tables that belong to no section, which is where every table opened
   * before sections existed still lives.
   */
  const [openHall, setOpenHall] = useState<string | null>(null)

  /**
   * The app's own dialogs, replacing window.prompt/confirm/alert.
   *
   * The browser's arrive in the browser's language, name the site in the
   * title bar, and on some phones cannot be dismissed by tapping away, so a
   * question written in Turkish came with an "OK" button and a hostname above
   * it. Same pair MenuAdminPage already uses.
   */
  const [ask, setAsk] = useState<ConfirmSpec | null>(null)
  const [prompt, setPrompt] = useState<PromptSpec | null>(null)
  const [bulkTables, setBulkTables] = useState(false)

  /** A message with nothing to decide, one button, no confirm handler. */
  const notify = (title: string, body: string) =>
    setAsk({ title, body, confirmLabel: 'Tamam' })
  const [newStaff, setNewStaff] = useState<StaffDraft | null>(null)
  const [staffBusy, setStaffBusy] = useState(false)
  const [staffError, setStaffError] = useState<string | null>(null)
  /** Person whose username/PIN is being reset. */
  const [reset, setReset] = useState<{
    person: AdminUser
    displayName: string
    username: string
    pin: string
    perms: Perms
  } | null>(null)

  useEffect(() => {
    if (!tenantId) return
    // Mutfak ekranlarının (tablet) hesapları burada listelenmiyor: onları
    // Mutfak ekranları bölümü yönetiyor; kullanıcı adları "ekran-" ile başlıyor
    // (functions/screens). Eskiden bütün 'kitchen' rolü gizleniyordu, elle
    // açılan mutfak personeli de listede görünmüyordu (20 Eki 2026).
    const a = subscribeStaff(
      tenantId,
      (list) => setStaff(list.filter((p) => p.role !== 'kiosk' && !(p.role === 'kitchen' && p.username.startsWith('ekran-')))),
      (e) => console.error('[settings] staff', e),
    )
    const b = subscribeTables(tenantId, setTables, (e) => console.error('[settings] tables', e))
    const bh = subscribeHalls(tenantId, setHalls, (e) => console.error('[settings] halls', e))
    return () => {
      a()
      b()
      bh()
    }
  }, [tenantId])

  const askAndSave = (
    label: string,
    current: string,
    save: (value: string) => Promise<void>,
  ) => {
    setPrompt({
      title: label.replace(/:$/, ''),
      label,
      initial: current,
      onConfirm: async (value) => {
        if (!value || value === current) return
        try {
          await save(value)
        } catch (err) {
          console.error('[settings]', err)
          notify('Kaydedilemedi', 'İnternet bağlantınızı kontrol edin.')
        }
      },
    })
  }



  // ---- receipt ----

  /**
   * Kasa fişi yeni panelden hiç kaydedilmediyse (splitByStation yok) ödeme
   * fişi bugün garson fişinin ayarlarıyla basılıyor. Sekme de onu göstererek
   * açılıyor: alanlar o ayarlarla dolu. Patron Kaydet diyene kadar basılan fiş
   * değişmiyor.
   */
  const cashReady = cashReceiptSettings.splitByStation !== undefined
  const cashSeed: ReceiptSettings = cashReady
    ? cashReceiptSettings
    : { ...DEFAULT_RECEIPT, ...receiptSettings, splitByStation: true }
  const savedCfg = receiptTab === 'cash' ? cashSeed : receiptSettings
  const receiptCfg = receiptDraft ?? savedCfg
  const receiptDirty = receiptDraft !== null
  const receiptDefaults = receiptTab === 'cash' && cashReady ? DEFAULT_CASH_RECEIPT : DEFAULT_RECEIPT

  /** Sekme değişirken kaydedilmemiş düzenleme varsa sor. */
  const switchReceiptTab = (next: 'cash' | 'station') => {
    if (next === receiptTab) return
    if (!receiptDirty) {
      setReceiptTab(next)
      return
    }
    setAsk({
      title: 'Kaydedilmemiş fiş değişiklikleri var',
      body: 'Diğer fişe geçmeden önce kaydetmek ister misiniz?',
      closeX: true,
      altLabel: 'Kaydetmeden devam et',
      onAlt: () => {
        setReceiptDraft(null)
        setReceiptTab(next)
      },
      confirmLabel: 'Kaydet ve geç',
      onConfirm: () => {
        void onSaveReceipt().then((ok) => ok && setReceiptTab(next))
      },
    })
  }

  const editReceipt = (patch: Partial<ReceiptSettings>) =>
    setReceiptDraft({ ...receiptCfg, ...patch })

  /**
   * Opens a section and closes whichever was open.
   *
   * One at a time, so the page stays a short list of headings, with several
   * expanded the receipt panel alone pushed everything below it off screen,
   * which is what the folding was for in the first place.
   *
   * Closing the receipt panel mid-edit would drop the draft, so that one asks
   * first, the same guard the two receipt tabs already use between them.
   */
  const toggleSection = (id: string) => {
    // Any press while the receipt panel is open and dirty closes it, either by
    // collapsing it or by opening something else, so both cases ask.
    //
    // The dialog answers later rather than blocking here, so the move is
    // deferred into its handler instead of running past the question.
    if (openSection === 'receipt' && receiptDirty) {
      setAsk({
        title: 'Kaydedilmemiş fiş değişiklikleri var',
        body: 'Kapatmadan önce kaydetmek ister misiniz?',
        closeX: true,
        altLabel: 'Kaydetmeden kapat',
        onAlt: () => {
          setReceiptDraft(null)
          setOpenSection((current) => (current === id ? null : id))
        },
        confirmLabel: 'Kaydet ve kapat',
        onConfirm: () => {
          void onSaveReceipt().then(
            (ok) => ok && setOpenSection((current) => (current === id ? null : id)),
          )
        },
      })
      return
    }
    setOpenSection((current) => (current === id ? null : id))
  }

  /** Kaydeder; başarılıysa true (sorudan "Kaydet ve geç" bunu bekliyor). */
  const onSaveReceipt = async (): Promise<boolean> => {
    if (!tenantId || !receiptDraft) return false
    setReceiptBusy(true)
    try {
      await saveTenantSettings(
        tenantId,
        receiptTab === 'cash'
          ? // splitByStation: kasa fişinin bu panelden kaydedildiğinin işareti; her zaman bölünüyor.
            { cashReceipt: { ...receiptDraft, splitByStation: true } }
          : { receipt: receiptDraft },
      )
      setReceiptDraft(null)
      return true
    } catch (err) {
      console.error('[settings] receipt', err)
      notify('Kaydedilemedi', 'İnternet bağlantınızı kontrol edin.')
      return false
    } finally {
      setReceiptBusy(false)
    }
  }

  // ---- staff ----

  const onCreateStaff = async () => {
    if (!tenantId || !newStaff) return
    // Paketin kasa ve garson sınırı (29 Eki 2026): eklemez, yükseltme penceresi açar.
    if (newStaff.role !== 'kitchen' && staffFull) {
      setNewStaff(null)
      askUpgrade('staff')
      return
    }
    setStaffBusy(true)
    setStaffError(null)
    try {
      await createStaff(tenantId, newStaff)
      // Yeni kişi kendi rolünün sekmesinde görünsün.
      setUsersTab(newStaff.role)
      setNewStaff(null)
    } catch (err) {
      setStaffError(err instanceof Error ? err.message : 'Kullanıcı eklenemedi.')
    } finally {
      setStaffBusy(false)
    }
  }

  const onResetCredentials = async () => {
    if (!tenantId || !reset) return

    const displayName = reset.displayName.trim()
    // An owner may rename themselves, but their login is never rebuilt here:
    // that path deletes and recreates the Auth account, and the last owner
    // getting it wrong would leave nobody able to administer the yurt.
    const isOwner = reset.person.role === 'owner'
    const usernameChanged = !isOwner && reset.username.trim() !== reset.person.username
    const pinChanged = !isOwner && reset.pin.trim() !== ''

    setStaffBusy(true)
    setStaffError(null)
    try {
      // Only rebuild the account when the login itself changes. Renaming
      // someone shouldn't cost them their PIN.
      if (usernameChanged || pinChanged) {
        if (isOwner) return // guarded above; the type below excludes 'owner'
        if (!pinChanged) {
          setStaffError('Kullanıcı adı değişince yeni PIN de girilmeli.')
          return
        }
        await resetStaffCredentials(
          tenantId,
          {
            uid: reset.person.uid,
            username: reset.person.username,
            displayName,
            role: reset.person.role as Exclude<Role, 'owner'>,
          },
          { username: reset.username, pin: reset.pin },
        )
      } else if (displayName !== (reset.person.displayName ?? '')) {
        await saveStaffDisplayName(tenantId, reset.person.uid, displayName)
      }

      // Written separately from the name, and after it: rebuilding a login
      // replaces the row's id, so a permission written before that would be
      // attached to an account that no longer exists.
      const before = reset.person.perms ?? {}
      const changed =
        PERMS.some((p) => (before[p.key] ?? false) !== (reset.perms[p.key] ?? false)) ||
        (before.station ?? '') !== (reset.perms.station ?? '') ||
        [...(before.halls ?? [])].sort().join() !== [...(reset.perms.halls ?? [])].sort().join()
      if (changed) {
        await saveStaffPerms(tenantId, reset.person.uid, reset.perms)
      }

      setReset(null)
    } catch (err) {
      setStaffError(err instanceof Error ? err.message : 'Değiştirilemedi.')
    } finally {
      setStaffBusy(false)
    }
  }

  /**
   * Yetkiyi kartın üstünden tek dokunuşla değiştirir (kullanıcı, 13 Eki 2026).
   * Önce ekranda (permDraft), sonra sunucuda; olmazsa geri alınıyor.
   */
  /** Kullanıcılar listesi: patron hariç. */
  // Müdür ötekini göremez ve düzenleyemez: yalnız patron müdür açar.
  const team = staff.filter((p) => p.role !== 'owner' && (viewerOwner || p.role !== 'manager'))
  /** Paketin sınırına sayılanlar: kasa ve garson (plans.ts). */
  // Müdür de sayılır (3 Kas 2026): yoksa sınırın etrafından dolaşılırdı.
  const counted = staff.filter((p) => p.role === 'cashier' || p.role === 'waiter' || p.role === 'manager').length
  const staffFull = staffLimit !== null && counted >= staffLimit
  /**
   * Kullanıcılar rol sekmelerinde (kullanıcı, 23 Eki 2026), Masa
   * Yönetimi'ndeki salon sekmeleri gibi. Kasa ve Garson hep var; Mutfak
   * yalnız görünür bir mutfak kullanıcısı varsa.
   */
  const roleTabs = (['manager', 'cashier', 'waiter', 'kitchen'] as const).filter(
    (r) => (r !== 'kitchen' && r !== 'manager') || team.some((p) => p.role === r),
  )
  const roleTone = { manager: 'violet', cashier: 'green', waiter: 'blue', kitchen: 'amber' } as const
  const [usersTab, setUsersTab] = useState<'manager' | 'cashier' | 'waiter' | 'kitchen' | null>(null)
  // Seçilmemişse kullanıcısı olan ilk sekme.
  const shownRole = usersTab ?? roleTabs.find((r) => team.some((p) => p.role === r)) ?? 'cashier'
  const [permDraft, setPermDraft] = useState<Record<string, Perms>>({})
  /** Yetki penceresi açık olan personelin kimliği. */
  const [permPerson, setPermPerson] = useState<string | null>(null)
  const togglePerm = async (person: AdminUser, key: PermKey, value: boolean) => {
    if (!tenantId) return
    const next: Perms = { ...(person.perms ?? {}), ...(permDraft[person.uid] ?? {}), [key]: value }
    setPermDraft((d) => ({ ...d, [person.uid]: next }))
    try {
      await saveStaffPerms(tenantId, person.uid, next)
    } catch (err) {
      console.error('[settings] perm', err)
      setPermDraft((d) => {
        const { [person.uid]: _drop, ...rest } = d
        return rest
      })
      notify('Kaydedilemedi', 'İnternet bağlantınızı kontrol edin.')
    }
  }

  const onRemoveStaff = async (person: AdminUser) => {
    if (!tenantId) return
    setAsk({
      title: `${person.displayName || person.username} kaldırılsın mı?`,
      body: 'Artık giriş yapamayacak. Geçmiş satışları ve adisyonları raporda kalır.',
      confirmLabel: 'Kaldır',
      danger: true,
      onConfirm: async () => {
        try {
          await removeStaff(tenantId, person.uid, person.username)
        } catch (err) {
          console.error('[settings] remove staff', err)
          notify('Kaldırılamadı', 'İnternet bağlantınızı kontrol edin.')
        }
      },
    })
  }

  // ---- tables ----

  /** Tables in the section on screen; `null` gathers the ones in no section. */
  const shownTables = tables.filter((t) => (t.hallId ?? null) === openHall)

  /** Her salonun kendi rengi (utils/hallTones, 22 Eki 2026). */
  const toneOf = (id: string | null) => hallTone(id, halls)

  /** How many tables belong to no section. Zero hides the "Salonsuz" tab. */
  const looseTables = tables.filter((t) => !t.hallId).length

  useEffect(() => {
    // The Salonsuz tab disappears once its last table is moved or deleted;
    // standing on it then leaves the screen on a tab that is no longer
    // there, so the first section takes over.
    if (openHall === null && looseTables === 0 && halls.length > 0) {
      setOpenHall(halls[0].id)
    }
  }, [openHall, looseTables, halls])

  const nextTableOrder = () =>
    tables.length ? Math.max(...tables.map((t) => t.sortOrder)) + 10 : 10

  /**
   * Aynı salonda aynı adlı masa var mı (28 Eki 2026). Farklı salonlarda
   * serbest: sipariş masayı "Salon → Masa" diye tutuyor, karışmıyor. Aynı
   * salonda iki "Masa 1" ise iki adisyonu tek masaya düşürürdü.
   */
  const tableNameTaken = (name: string, hallId: string | undefined, exceptId?: string) => {
    const key = name.trim().toLocaleLowerCase('tr-TR')
    return tables.some(
      (t) =>
        t.id !== exceptId &&
        (t.hallId ?? null) === (hallId ?? null) &&
        t.name.trim().toLocaleLowerCase('tr-TR') === key,
    )
  }
  const hallTitle = (hallId: string | undefined) =>
    halls.find((h) => h.id === hallId)?.name ?? 'Salonsuz'

  const onAddTable = async () => {
    if (!tenantId) return
    setPrompt({
      title: 'Yeni masa',
      label: 'Masa adı',
      placeholder: 'Örn. 1, A3, Bahçe 1',
      confirmLabel: 'Ekle',
      onConfirm: async (name) => {
        if (tableNameTaken(name, openHall ?? undefined)) {
          notify('Bu salonda bu isimde masa var', `${hallTitle(openHall ?? undefined)} içinde "${name}" zaten açılmış.`)
          return
        }
        try {
          // Lands in whichever section is open, so adding to "Bahçe" does not
          // mean adding somewhere else and dragging it over afterwards.
          await saveTable(tenantId, {
            name,
            sortOrder: nextTableOrder(),
            active: true,
            hallId: openHall ?? undefined,
          })
        } catch (err) {
          console.error('[settings] table', err)
          notify('Masa eklenemedi', 'İnternet bağlantınızı kontrol edin.')
        }
      },
    })
  }

  /**
   * Opening a room at once.
   *
   * Twenty tables added one at a time is twenty prompts and twenty writes; a
   * new restaurant does this on its first day and it is the worst first
   * impression the app can make. Asks for the count and the naming so a place
   * that calls them "A1" is not left renaming twenty rows.
   */
  const onAddManyTables = async (count: number, template: string) => {
    if (!tenantId) return
    setBulkTables(false)

    let order = nextTableOrder()
    let made = 0
    // Salonda zaten olan adlar atlanıyor ve söyleniyor (28 Eki 2026).
    const skipped: string[] = []
    try {
      for (let i = 1; i <= count; i++) {
        const name = template.replace(/#/g, String(i))
        if (tableNameTaken(name, openHall ?? undefined)) {
          skipped.push(name)
          continue
        }
        await saveTable(tenantId, {
          name,
          sortOrder: order,
          active: true,
          hallId: openHall ?? undefined,
        })
        order += 10
        made++
      }
      if (skipped.length > 0) {
        notify(
          `${skipped.length} masa zaten vardı, atlandı`,
          `${hallTitle(openHall ?? undefined)} içinde ${skipped.join(', ')} açılmış. ${made} yeni masa eklendi.`,
        )
      }
    } catch (err) {
      console.error('[settings] tables bulk', err)
      // Says how far it got: the writes go one at a time, so a failure
      // halfway leaves real tables behind and a bare "eklenemedi" would
      // invite adding the whole set a second time.
      notify(
        'Masalar eklenirken hata oluştu',
        `${made} masa eklendi, listede görünüyor. Kalanı için tekrar deneyin.`,
      )
    }
  }

  // ---- halls ----

  const onAddHall = async () => {
    if (!tenantId) return
    setPrompt({
      title: 'Yeni salon',
      label: 'Salon adı',
      placeholder: 'Örn. Bahçe, Üst Kat, Teras',
      confirmLabel: 'Ekle',
      onConfirm: async (name) => {
        // Two sections with one name are indistinguishable on the tabs, and
        // the tables in them cannot be told apart afterwards either, the
        // second one is a mis-tap, not a decision.
        if (halls.some((h) => h.name.toLocaleLowerCase('tr-TR') === name.toLocaleLowerCase('tr-TR'))) {
          notify('Bu isimde bir salon zaten var', `"${name}" adlı salon açılmış durumda.`)
          return
        }
        const sortOrder = halls.length ? Math.max(...halls.map((h) => h.sortOrder)) + 10 : 10
        try {
          const id = await saveHall(tenantId, { name, sortOrder })
          // Straight into the new section: it was opened in order to put
          // tables in it, and leaving the old tab selected hides what just
          // happened.
          setOpenHall(id)
        } catch (err) {
          console.error('[settings] hall', err)
          notify('Salon eklenemedi', 'İnternet bağlantınızı kontrol edin.')
        }
      },
    })
  }

  const onRenameHall = (hall: Hall) =>
    askAndSave('Salon adı:', hall.name, async (name) => {
      const key = name.toLocaleLowerCase('tr-TR')
      if (halls.some((h) => h.id !== hall.id && h.name.toLocaleLowerCase('tr-TR') === key)) {
        notify('Bu isimde bir salon zaten var', `"${name}" adlı salon açılmış durumda.`)
        return
      }
      await saveHall(tenantId!, { ...hall, name })
    })

  const onDeleteHall = async (hall: Hall) => {
    if (!tenantId) return
    const inside = tables.filter((t) => t.hallId === hall.id).length
    setAsk({
      title: `"${hall.name}" salonu silinsin mi?`,
      body: (
        <>
          {inside > 0 && <>İçindeki {inside} masa silinmez, salonsuz kalır. </>}
          Geçmiş adisyonlar etkilenmez.
        </>
      ),
      confirmLabel: 'Sil',
      danger: true,
      onConfirm: async () => {
        try {
          await deleteHall(tenantId, hall.id)
          if (openHall === hall.id) setOpenHall(null)
        } catch (err) {
          console.error('[settings] hall delete', err)
          notify('Salon silinemedi', 'İnternet bağlantınızı kontrol edin.')
        }
      },
    })
  }

  const onRenameTable = (table: Table) =>
    askAndSave('Masa adı:', table.name, async (name) => {
      if (tableNameTaken(name, table.hallId, table.id)) {
        notify('Bu salonda bu isimde masa var', `${hallTitle(table.hallId)} içinde "${name}" zaten açılmış.`)
        return
      }
      await saveTable(tenantId!, { ...table, name })
    })

  const onToggleTable = async (table: Table) => {
    if (!tenantId) return
    try {
      await saveTable(tenantId, { ...table, active: !table.active })
    } catch (err) {
      console.error('[settings] table toggle', err)
    }
  }

  /**
   * "Salonsuz" bir salon değil, salonu olmayan masaların toplandığı yer; onu
   * silmek içindeki masaları silmek demek (kullanıcı, 22 Eki 2026). Açık
   * adisyonu olan masa tek tek silmedeki gibi atlanıyor ve söyleniyor.
   */
  const onDeleteLooseTables = () => {
    if (!tenantId) return
    const loose = tables.filter((t) => !t.hallId)
    const busy = loose.filter((t) => openTickets.some((o) => o.tableName === tableLabel(t, halls)))
    const free = loose.filter((t) => !busy.includes(t))
    if (free.length === 0) {
      setAsk({
        title: 'Salonsuz masaların hepsinde açık adisyon var',
        body: 'Önce kasada ödemeleri alıp kapatın ya da adisyonları başka masaya taşıyın.',
        confirmLabel: 'Tamam',
      })
      return
    }
    setAsk({
      title: `Salonsuz ${free.length} masa silinsin mi?`,
      body: (
        <>
          {busy.length > 0 && (
            <>Açık adisyonu olan {busy.map((t) => t.name).join(', ')} silinmez. </>
          )}
          Geçmiş adisyonlar etkilenmez.
        </>
      ),
      confirmLabel: 'Sil',
      danger: true,
      onConfirm: async () => {
        let done = 0
        try {
          for (const t of free) {
            await deleteTable(tenantId, t.id)
            done++
          }
        } catch (err) {
          console.error('[settings] loose tables delete', err)
          notify('Masaların bir kısmı silinemedi', `${done} masa silindi. Kalanı için tekrar deneyin.`)
        }
      },
    })
  }

  const onDeleteTable = async (table: Table) => {
    if (!tenantId) return
    // Açık adisyonu olan masa silinmiyor (21 Eki 2026): silinince adisyon
    // kasada hiçbir masada görünmüyor, ödeme sistemden alınamıyordu.
    const open = openTickets.find((o) => o.tableName === tableLabel(table, halls))
    if (open) {
      setAsk({
        title: `"${table.name}" masasında açık adisyon var`,
        body: 'Önce kasada ödemeyi alıp kapatın ya da adisyonu başka masaya taşıyın, sonra silin.',
        confirmLabel: 'Tamam',
      })
      return
    }
    setAsk({
      title: `"${table.name}" masası silinsin mi?`,
      body: 'Geçmiş adisyonlar etkilenmez.',
      confirmLabel: 'Sil',
      danger: true,
      onConfirm: async () => {
        try {
          await deleteTable(tenantId, table.id)
        } catch (err) {
          console.error('[settings] table delete', err)
          notify('Silinemedi', 'İnternet bağlantınızı kontrol edin.')
        }
      },
    })
  }

  return (
    <div
      className={`settings-page ${only || openSection ? 'settings-page--wide' : ''} ${!only && !openSection ? 'settings-page--grid' : ''}`}
    >

      {/* En üstte: paketi ve deneme süresinin ne zaman bittiği. Yalnız patron. */}
      {see('hesap') && (
      <Section
        title="Hesabım"
        icon={<StoreIcon />}
        size="big"
        tone="brand"
        hint={trialEndsAt === null && plan ? `${plan.name} paketi` : trialSummary(trialEndsAt)}
        open={openSection === 'hesap'}
        anyOpen={openSection !== null}
        onToggle={() => toggleSection('hesap')}
      >
        <AccountPanel staff={staff} />
      </Section>
      )}

      {hasFeature('qr') && tenantId && see('qr') && (
        <Section
          title="QR Menü"
          icon={<QrCodeIcon />}
          tone="violet"
          hint="Müşterinin telefonundan gördüğü menü"
          open={openSection === 'qr'}
          anyOpen={openSection !== null}
          onToggle={() => toggleSection('qr')}
        >
          <QrMenuPanel tenantId={tenantId} />
        </Section>
      )}


      {/* The slip and its preview side by side: what to keep on a receipt is
          hard to decide in the abstract and obvious once it is drawn. */}
      {see('receipt') && (
      <Section
        title="Fiş"
        icon={<ReceiptTextIcon />}
        size="wide"
        tone="amber"
        hint="Kasa fişi ve garson fişi"
        open={openSection === 'receipt'}
        anyOpen={openSection !== null}
        onToggle={() => toggleSection('receipt')}
        actions={
          receiptDirty && (
            <div className="set-block__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setReceiptDraft(null)}
                disabled={receiptBusy}
              >
                Geri al
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={onSaveReceipt}
                disabled={receiptBusy}
              >
                {receiptBusy ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          )
        }
      >
        {/* İki fiş, iki ayar (16 Eki 2026). Eskiden burada iki sekme vardı ama
            kasa fişini basan bir yol yoktu, sekme kaldırılmıştı. Artık kasada
            ödeme, son fişi tekrar bas ve rapordan basılan fiş kasa fişi;
            garsonun girdiği siparişin ve açık adisyonun fişi garson fişi. */}
        <div className="rtabs" role="tablist" aria-label="Fiş türü">
          {(
            [
              ['cash', 'Kasa fişi', 'Kasada ödeme alınınca', <BanknoteIcon key="i" />],
              ['station', 'Garson fişi', 'Garsonun girdiği sipariş', <NotebookPenIcon key="i" />],
            ] as const
          ).map(([id, label, hint, icon]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={receiptTab === id}
              className={`rtabs__tab ${receiptTab === id ? 'is-on' : ''}`}
              onClick={() => switchReceiptTab(id)}
            >
              <span className="rtabs__icon">{icon}</span>
              <span className="rtabs__text">
                {label}
                <small>{hint}</small>
              </span>
            </button>
          ))}
          {/* Bilgi sekmelerin sağında (kullanıcı, 16 Eki 2026). */}
          <p className="rtabs__info">
            {receiptTab === 'cash' ? (
              <>
                <b>Kasa fişi:</b> kasada ödeme alınınca çıkan fiş. "Son fişi tekrar
                bas" ve rapordan basılan fiş de bu.
              </>
            ) : (
              <>
                <b>Garson fişi:</b> garsonun telefondan girdiği siparişin kasada
                basılan fişi. Açık adisyondan ödeme öncesi basılan fiş de bu.
              </>
            )}
          </p>
        </div>
        <div className="rcfg">
          <div className="rcfg__form">


            <label className="rcfg__field">
              Başlık
              <input
                value={receiptCfg.title ?? ''}
                onChange={(e) => editReceipt({ title: e.target.value })}
                placeholder={tenantName}
                maxLength={40}
              />
            </label>

            <label className="rcfg__field">
              Alt yazı
              <input
                value={receiptCfg.footer ?? receiptDefaults.footer}
                onChange={(e) => editReceipt({ footer: e.target.value })}
                placeholder="Boş bırakılırsa yazılmaz"
                maxLength={40}
              />
            </label>

            <div className="rcfg__checks">
              {(
                [
                  ['showReceiptNo', 'Fiş numarası'],
                  ['showDate', 'Tarih ve saat'],
                  ['showTable', 'Masa adı'],
                  ['showWaiter', 'Garson adı'],
                  ['showStationTotal', 'Tezgah adet satırı'],
                  ['showFooterStation', 'Altta tezgah adı'],
                  ['showPrices', 'Fiyatlar'],
                ] as const
              ).map(([key, label]) => {
                const on = receiptCfg[key] ?? receiptDefaults[key]
                return (
                  <label key={key} className={`rcfg__check ${on ? 'on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) => editReceipt({ [key]: e.target.checked })}
                    />
                    {label}
                  </label>
                )
              })}
            </div>

            {/* What a roll of paper is actually spent on. */}
            <label className="rcfg__field">
              Satır aralığı: %{receiptCfg.linePercent ?? receiptDefaults.linePercent}
              <input
                type="range"
                min={60}
                max={130}
                step={5}
                value={receiptCfg.linePercent ?? receiptDefaults.linePercent}
                onChange={(e) => editReceipt({ linePercent: Number(e.target.value) })}
              />
            </label>

            <label className="rcfg__field">
              Yazı boyutu: %{receiptCfg.fontPercent ?? receiptDefaults.fontPercent}
              <input
                type="range"
                min={70}
                max={130}
                step={5}
                value={receiptCfg.fontPercent ?? receiptDefaults.fontPercent}
                onChange={(e) => editReceipt({ fontPercent: Number(e.target.value) })}
              />
            </label>

            <label className="rcfg__field">
              Kesme payı: {receiptCfg.feedMm ?? receiptDefaults.feedMm} mm
              <input
                type="range"
                min={0}
                max={25}
                step={1}
                value={receiptCfg.feedMm ?? receiptDefaults.feedMm}
                onChange={(e) => editReceipt({ feedMm: Number(e.target.value) })}
              />
            </label>

            <p className="rcfg__hint">
              Kesme payı, son satırın kesme çubuğunu geçmesi için beslenen boş
              kağıt. Çok kısaltırsanız fişin sonu makinenin içinde kalır.
            </p>

            <label className="rcfg__field">
              Çizgi kalınlığı: {receiptCfg.ruleWidthPx ?? receiptDefaults.ruleWidthPx} px
              <input
                type="range"
                min={1}
                max={5}
                step={1}
                value={receiptCfg.ruleWidthPx ?? receiptDefaults.ruleWidthPx}
                onChange={(e) => editReceipt({ ruleWidthPx: Number(e.target.value) })}
              />
            </label>

            <label className="rcfg__field">
              Çizgi türü
              {/* Each option draws itself. A dropdown listing "Kesik" and
                  "Noktalı" asks the reader to picture the difference; at the
                  weights this prints at, seeing them side by side is the only
                  way to choose. */}
              <div className="rulepick">
                {RULE_STYLES.map(([value, label]) => {
                  const on = (receiptCfg.ruleStyle ?? receiptDefaults.ruleStyle) === value
                  return (
                    <button
                      key={value}
                      type="button"
                      className={`rulepick__opt ${on ? 'is-on' : ''}`}
                      onClick={() => editReceipt({ ruleStyle: value })}
                      aria-pressed={on}
                    >
                      <span
                        className="rulepick__line"
                        style={{
                          borderTopWidth: `${receiptCfg.ruleWidthPx ?? receiptDefaults.ruleWidthPx}px`,
                          borderTopStyle: value,
                        }}
                      />
                      {label}
                    </button>
                  )
                })}
              </div>
            </label>

            <p className="rcfg__hint">
              Termal kağıtta ince ve kesik çizgiler soluk basabilir. Emin
              değilseniz düz ve 2 px bırakın; yazdırıp görmek en kesini.
            </p>

          </div>

          <div className="rcfg__preview">
            <span className="rcfg__preview-label">Örnek fiş</span>
            <ReceiptPreview
              settings={receiptCfg}
              tenantName={tenantName}
              defaults={receiptDefaults}
            />

            {/* On paper, not on screen: thermal printing is where a hairline
                rule turns grey and a dashed one turns to specks, and no
                preview can show that. */}
            <button
              type="button"
              className="btn btn--outline rcfg__test"
              // Günü basılırken: sabit örnekte boştu, fişte "undefined.undefined." çıkıyordu.
              onClick={() =>
                onPrint({ ...TEST_ORDER, dayKey: toDayKey() }, receiptTab === 'cash' ? 'cash' : 'station')
              }
              disabled={receiptDirty}
              title={
                receiptDirty
                  ? 'Önce Kaydet: deneme fişi kayıtlı ayarlarla basılır'
                  : 'Bu ayarlarla bir deneme fişi bas'
              }
            >
              Deneme fişi yazdır
            </button>
            {receiptDirty && (
              <p className="rcfg__hint">
                Deneme fişi kayıtlı ayarlarla basılır. Önce Kaydet deyin.
              </p>
            )}
          </div>
        </div>

        {/* Kaydet ekranın altında da (kullanıcı, 16 Eki 2026): başlıktaki
            düğmeler uzun formun aşağısında görünmüyordu. */}
        {receiptDirty && (
          <div className="rcfg__savebar">
            <span>
              <b>{receiptTab === 'cash' ? 'Kasa fişinde' : 'Garson fişinde'}</b> kaydedilmemiş
              değişiklik var
            </span>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setReceiptDraft(null)}
              disabled={receiptBusy}
            >
              Geri al
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void onSaveReceipt()}
              disabled={receiptBusy}
            >
              {receiptBusy ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        )}
      </Section>
      )}

      {/* Ağ yazıcıları (20 Eki 2026): liste kurumun, "bu bilgisayar basar"
          cihazın; ikisi bir arada, kurulum bir yerden yapılsın. */}
      {see('printers') && (
      <Section
        title="Yazıcılar"
        icon={<PrinterIcon />}
        size="wide"
        tone="amber"
        hint={
          !usePrinter
            ? 'Yazıcısız, fiş basılmıyor'
            : printers.length > 0
              ? `${printers.length} yazıcı`
              : 'Ethernet fiş yazıcıları'
        }
        open={openSection === 'printers'}
        anyOpen={openSection !== null}
        onToggle={() => toggleSection('printers')}
      >
        {/* Eski Sistem bölümünün cihaz ayarları da burada (kullanıcı, 20 Eki
            2026): hepsi "bu bilgisayar basar mı" sorusunun parçası. */}
        <UsePrinterSwitch />
        {usePrinter ? <PrintersPanel deviceRows={<DeviceRows />} /> : <PrinterlessNote />}
      </Section>
      )}

      {only === 'tables' && (
      <Section
        title={only === 'tables' ? 'Masa Yönetimi' : 'Masalar'}
        hint={tables.length > 0 ? `${tables.length} masa` : undefined}
        open={openSection === 'tables'}
        anyOpen={openSection !== null}
        onToggle={() => toggleSection('tables')}
        standalone={only === 'tables'}
        actions={
          <div className="set-page-head__actions">
            {/* Menü sayfasındaki "+ Kategori" ile aynı düzen ve boy: artı, ayraç, etiket. */}
            <button type="button" className="btn btn--ghost" onClick={onAddHall}>
              <PlusIcon />
              <i className="btn__rule" />
              Salon
            </button>
            <button type="button" className="btn btn--ghost" onClick={onAddTable}>
              <PlusIcon />
              <i className="btn__rule" />
              Masa
            </button>
          </div>
        }
      >
        {/* Tabs, but only once there is a section to switch to: a place with
            one undivided room should not be asked to think about sections at
            all. The "Salonsuz" tab appears beside them rather than being
            hidden, tables opened before sections existed live there and have
            to stay reachable. */}
        {halls.length > 0 && (
          <div className="halls" role="tablist" aria-label="Salonlar">
            {/* Only while something is in it. Empty, it is a tab that leads
                to an empty room and says nothing, every table has a section
                and there is nothing left over to reach. */}
            {looseTables > 0 && (
              <div
                role="tab"
                aria-selected={openHall === null}
                className={`halls__tab tone--slate ${openHall === null ? 'is-on' : ''}`}
              >
                <button
                  type="button"
                  className="halls__pick"
                  onClick={() => setOpenHall(null)}
                >
                  Salonsuz
                  <span className="halls__count">{looseTables}</span>
                </button>
                {openHall === null && (
                  <MoreMenu
                    className="halls__more"
                    align="start"
                    label="Salonsuz masalar için işlemler"
                    items={[
                      { label: 'Toplu masa ekle', icon: <PlusIcon />, onSelect: () => setBulkTables(true) },
                      {
                        label: 'Masaları sil',
                        icon: <TrashIcon />,
                        danger: true,
                        divided: true,
                        onSelect: onDeleteLooseTables,
                      },
                    ]}
                  />
                )}
              </div>
            )}
            {halls.map((h) => (
              /* A div, not a button: renaming and deleting are buttons of
                 their own and nesting those inside one is invalid markup.
                 The tab's own press target is the name beside them. */
              <div
                key={h.id}
                role="tab"
                aria-selected={openHall === h.id}
                className={`halls__tab tone--${toneOf(h.id)} ${openHall === h.id ? 'is-on' : ''}`}
              >
                <button
                  type="button"
                  className="halls__pick"
                  onClick={() => setOpenHall(h.id)}
                >
                  {h.name}
                  <span className="halls__count">
                    {tables.filter((t) => t.hallId === h.id).length}
                  </span>
                </button>

                {/* Yalnız açık sekmede: beş salonda beş "⋯" satırı kalabalık
                    eder, işlem bakılan salon için. */}
                {openHall === h.id && (
                  <MoreMenu
                    className="halls__more"
                    align="start"
                    label={`${h.name} salonu için işlemler`}
                    items={[
                      { label: 'Adını değiştir', icon: <PencilIcon />, onSelect: () => onRenameHall(h) },
                      { label: 'Toplu masa ekle', icon: <PlusIcon />, onSelect: () => setBulkTables(true) },
                      {
                        label: 'Salonu sil',
                        icon: <TrashIcon />,
                        danger: true,
                        divided: true,
                        onSelect: () => onDeleteHall(h),
                      },
                    ]}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {shownTables.length === 0 ? (
          <div className="set-empty">
            <p>
              {halls.length > 0 && openHall !== null
                ? 'Bu salonda masa yok.'
                : 'Masa eklenmemiş. Garson sipariş alırken masayı buradan seçer.'}
            </p>
            {/* Offered where the room is empty, which is exactly where opening
                twenty tables one prompt at a time would hurt most. */}
            <button type="button" className="btn btn--ghost" onClick={() => setBulkTables(true)}>
              Toplu masa ekle
            </button>
          </div>
        ) : (
          /* Kare kartlar (kullanıcı, 22 Eki 2026, "Mock A"): masa adı büyük,
             işlemler köşedeki "⋯" menüsünde, gizli masa soluk ve kesikli.
             Sonda yeni masa kartı; tek tek ya da toplu. */
          <div className={`tcards tone--${halls.length > 0 ? toneOf(openHall) : 'brand'}`}>
            {shownTables.map((t) => (
              <div key={t.id} className={`tcard ${t.active ? '' : 'is-off'}`}>
                <MoreMenu
                  className="tcard__more"
                  label={`${t.name} için işlemler`}
                  items={[
                    { label: 'Adını değiştir', icon: <PencilIcon />, onSelect: () => onRenameTable(t) },
                    t.active
                      ? { label: 'Gizle', icon: <EyeOffIcon />, onSelect: () => onToggleTable(t) }
                      : { label: 'Kullanıma aç', icon: <EyeIcon />, onSelect: () => onToggleTable(t) },
                    {
                      label: 'Sil',
                      icon: <TrashIcon />,
                      danger: true,
                      divided: true,
                      onSelect: () => onDeleteTable(t),
                    },
                  ]}
                />
                <span className="tcard__icon" aria-hidden="true">
                  <TableIcon />
                </span>
                <span className="tcard__text">
                  <b className="tcard__name">{t.name}</b>
                  <small className="tcard__tag" title={t.active ? undefined : 'Garson sipariş alırken bu masayı görmez'}>
                    {t.active ? 'Kullanımda' : 'Gizli'}
                  </small>
                </span>
              </div>
            ))}
            <div className="tcard tcard--add">
              <button type="button" className="tcard__add" onClick={onAddTable}>
                <PlusIcon />
                Masa ekle
              </button>
              <button type="button" className="tcard__bulk" onClick={() => setBulkTables(true)}>
                Toplu ekle
              </button>
            </div>
          </div>
        )}
      </Section>
      )}

      {see('staff') && (
      <Section
        title="Kullanıcılar"
        icon={<UserIcon />}
        size="wide"
        tone="blue"
        hint={
          staffLimit !== null
            ? `${counted} / ${staffLimit} kasa ve garson`
            : team.length > 0
              ? `${team.length} kişi`
              : undefined
        }
        open={openSection === 'staff'}
        anyOpen={openSection !== null}
        onToggle={() => toggleSection('staff')}
        actions={
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              // Sınır dolu ve mutfak hesabı da açılamıyorsa pencereye gerek yok.
              if (staffFull && !hasFeature('mutfak')) {
                askUpgrade('staff')
                return
              }
              setStaffError(null)
              setNewStaff(staffFull ? { ...emptyStaff, role: 'kitchen' } : emptyStaff)
            }}
          >
            {/* Menü'deki "+ Kategori" ile aynı düzen: artı, ayraç, etiket. */}
            <PlusIcon />
            <i className="btn__rule" />
            Kullanıcı
          </button>
        }
      >
        {team.length === 0 ? (
          <p className="set-empty">Henüz kullanıcı yok. Garson ve kasa için sağ üstteki "Kullanıcı" ile ekleyin.</p>
        ) : (
          /* Patron listede yok (kullanıcı, 13 Eki 2026: adını buradan
             düzeltmeye gerek kalmadı). Öteki kullanıcılar kart; yetkiler
             kartın üstünde tek dokunuşla açılıp kapanıyor. */
          <>
          <div className="halls" role="tablist" aria-label="Roller">
            {roleTabs.map((r) => (
              <div
                key={r}
                role="tab"
                aria-selected={shownRole === r}
                className={`halls__tab tone--${roleTone[r]} ${shownRole === r ? 'is-on' : ''}`}
              >
                <button type="button" className="halls__pick" onClick={() => setUsersTab(r)}>
                  {roleLabels[r]}
                  <span className="halls__count">{team.filter((p) => p.role === r).length}</span>
                </button>
              </div>
            ))}
          </div>
          {!team.some((p) => p.role === shownRole) ? (
            <p className="set-empty">Bu rolde kullanıcı yok. Sağ üstteki "Kullanıcı" ile ekleyin.</p>
          ) : (
          <div className="ucards">
            {team
              .filter((p) => p.role === shownRole)
              .sort((x, y) =>
                (x.displayName || x.username).localeCompare(y.displayName || y.username, 'tr'),
              )
              .map((person) => {
                const name = person.displayName || person.username
                const perms = { ...(person.perms ?? {}), ...(permDraft[person.uid] ?? {}) }
                const options = PERMS.filter(
                  (perm) => perm.roles.includes(person.role) && (!perm.feature || hasFeature(perm.feature)),
                )
                return (
                  <article
                    key={person.uid}
                    className={`ucard ${person.role === 'owner' ? 'ucard--owner' : ''} ${options.length > 0 ? 'is-clickable' : ''}`}
                    // Kartın boş yerine dokunmak da yetkileri açıyor; düğmeler kendi işini yapıyor.
                    onClick={(e) => {
                      if (options.length > 0 && !(e.target as HTMLElement).closest('button')) setPermPerson(person.uid)
                    }}
                  >
                    <header className="ucard__head">
                      <span className="ucard__avatar" aria-hidden="true">
                        {name.trim().charAt(0).toLocaleUpperCase('tr')}
                      </span>
                      <div className="ucard__who">
                        <b>{name}</b>
                        {/* Rol sekmede yazıyor; burada yalnız kullanıcı adı (25 Eki 2026). */}
                        <small>{person.username}</small>
                      </div>
                      {/* An owner may rename themselves but not be deleted, and
                          not have their login rebuilt: the last owner locking
                          themselves out would leave the yurt with no way in. */}
                      <div className="ucard__actions">
                        {options.length > 0 && (
                          <button
                            type="button"
                            className="icon-btn"
                            title="Yetkiler"
                            aria-label={`${name} yetkileri`}
                            onClick={() => setPermPerson(person.uid)}
                          >
                            <KeyRoundIcon />
                          </button>
                        )}
                        <button
                          type="button"
                          className="icon-btn"
                          title="Düzenle"
                          aria-label="Düzenle"
                          onClick={() => {
                            setStaffError(null)
                            setReset({
                              person,
                              displayName: person.displayName ?? '',
                              username: person.username,
                              pin: '',
                              perms: person.perms ?? {},
                            })
                          }}
                        >
                          <PencilIcon />
                        </button>
                        {person.role !== 'owner' && (
                          <button
                            type="button"
                            className="icon-btn is-danger"
                            title="Kaldır"
                            aria-label="Kaldır"
                            onClick={() => onRemoveStaff(person)}
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    </header>

                  </article>
                )
              })}
          </div>
          )}
          </>
        )}
      </Section>
      )}

      {/* Personel yetkileri penceresi (25 Eki 2026). */}
      {permPerson &&
        (() => {
          const person = staff.find((p) => p.uid === permPerson)
          if (!person) return null
          const name = person.displayName || person.username
          const perms = { ...(person.perms ?? {}), ...(permDraft[person.uid] ?? {}) }
          const options = PERMS.filter(
            (perm) => perm.roles.includes(person.role) && (!perm.feature || hasFeature(perm.feature)),
          )
          return (
            <div className="dlg" role="dialog" aria-modal="true" aria-label={`${name} yetkileri`}>
              <div className="dlg__card dlg__card--x uperm-dlg">
                <button type="button" className="dlg__x" onClick={() => setPermPerson(null)} aria-label="Kapat">
                  <XIcon />
                </button>
                <div className="uperm-dlg__who">
                  <span className="ucard__avatar" aria-hidden="true">
                    {name.trim().charAt(0).toLocaleUpperCase('tr')}
                  </span>
                  <div>
                    <h2>{name}</h2>
                    <small>{person.username}</small>
                  </div>
                </div>
                <PermChips
                  label={`${name} yetkileri`}
                  options={options}
                  isOn={(perm) => (perms[perm.key] ?? perm.defaultOn) === true}
                  onToggle={(perm, on) => void togglePerm(person, perm.key, on)}
                />
                <div className="dlg__buttons">
                  <button type="button" className="btn btn--primary" onClick={() => setPermPerson(null)}>
                    Tamam
                  </button>
                </div>
              </div>
            </div>
          )
        })()}

      {/* Kampanyalar: kasada öneri olarak çıkan indirim kuralları (14 Eki 2026). */}
      {tenantId && see('kampanya') && (
        <Section
          title="Kampanyalar"
          icon={<GiftIcon />}
          tone="amber"
          hint="Kasada çıkan indirim önerileri"
          open={openSection === 'kampanya'}
          anyOpen={openSection !== null}
          onToggle={() => toggleSection('kampanya')}
        >
          <CampaignsPanel tenantId={tenantId} />
        </Section>
      )}

      {/* Mutfak ekranları: cihaz olarak bağlanan tabletler (11 Eki 2026). */}
      {tenantId && see('screens') && (
        <Section
          title="Mutfak ekranları"
          icon={<ChefHatIcon />}
          tone="green"
          hint="Tezgah siparişlerini gösteren tabletler"
          open={openSection === 'screens'}
          anyOpen={openSection !== null}
          onToggle={() => toggleSection('screens')}
        >
          {hasFeature('mutfak') ? (
            <>
              <KitchenBellSetting />
              <KitchenScreensPanel tenantId={tenantId} />
            </>
          ) : (
            <LockedNote feature="mutfak" />
          )}
        </Section>
      )}

      {/* Müşteri sadakat (30 Eki 2026): ek modül 'damga'; yoksa bölüm yok. */}
      {tenantId && hasFeature('damga') && see('sadakat') && (
        <Section
          title="Sadakat"
          icon={<HeartIcon />}
          tone="brand"
          hint="Damga kartı ve ödül"
          open={openSection === 'sadakat'}
          anyOpen={openSection !== null}
          onToggle={() => toggleSection('sadakat')}
        >
          <LoyaltyPanel />
        </Section>
      )}

      {/* Kiosk (30 Eki 2026): ek modül; yoksa bölüm hiç görünmüyor. */}
      {tenantId && hasFeature('kiosk') && see('kiosk') && (
        <Section
          title="Kiosk"
          icon={<TabletIcon />}
          tone="brand"
          hint="Müşterinin kendi sipariş verdiği tabletler"
          open={openSection === 'kiosk'}
          anyOpen={openSection !== null}
          onToggle={() => toggleSection('kiosk')}
        >
          <KitchenScreensPanel tenantId={tenantId} kind="kiosk" />
        </Section>
      )}


      {reset && (
        <div className="dlg" role="dialog" aria-modal="true">
          <form
            className="dlg__card"
            onSubmit={(e) => {
              e.preventDefault()
              void onResetCredentials()
            }}
          >
            <h2>Kullanıcıyı Düzenle</h2>

            <label className="dlg__field">
              Görünen ad
              <input
                value={reset.displayName}
                onChange={(e) => setReset({ ...reset, displayName: e.target.value })}
                placeholder="Ali Yılmaz"
              />
            </label>

            {/* Hidden for an owner rather than shown and ignored: rebuilding
                the login deletes and recreates the Auth account, and the last
                owner mistyping it would lock the yurt out of its own settings. */}
            {reset.person.role !== 'owner' && (
              <>
                <label className="dlg__field">
                  Kullanıcı adı
                  <input
                    value={reset.username}
                    onChange={(e) => setReset({ ...reset, username: e.target.value })}
                    autoCapitalize="none"
                    spellCheck={false}
                    required
                  />
                </label>

                <label className="dlg__field">
                  Yeni PIN (değiştirmiyorsanız boş bırakın)
                  <input
                    value={reset.pin}
                    onChange={(e) =>
                      setReset({ ...reset, pin: e.target.value.replace(/\D/g, '').slice(0, 8) })
                    }
                    inputMode="numeric"
                    placeholder="4-8 rakam"
                  />
                </label>
              </>
            )}

            {reset.person.role === 'kitchen' && (
              <label className="dlg__field">
                Tezgahı
                <Select
                  value={reset.perms.station ?? ''}
                  onChange={(v) => setReset({ ...reset, perms: { ...reset.perms, station: v } })}
                  aria-label="Tezgahı"
                  options={stationOptions}
                />
              </label>
            )}

            {/* Salon seç (28 Eki 2026, kullanıcı): garson yalnız seçilen
                salonların masalarını görür ve yalnız onlara taşır. Hiçbiri
                seçili değilse bütün salonlar, eskisi gibi. 3 Kas 2026: kasiyer
                de (her salona ayrı kasa). */}
            {(reset.person.role === 'waiter' || reset.person.role === 'cashier') && halls.length > 0 && (
              <div className="dlg__field">
                <span>Salon seç</span>
                <div className="fchips hallpick" role="group" aria-label="Salon seç">
                  <div className="fchips__list">
                    {halls.map((h) => {
                      const picked = (reset.perms.halls ?? []).filter((id) => halls.some((x) => x.id === id))
                      // Kasiyer (3 Kas 2026, kullanıcı): boş liste "hepsi" ve
                      // hepsi işaretli görünür; patron fazlasını kaldırarak azaltır.
                      // Hepsi yeniden işaretlenince liste boşa döner, son salon
                      // kaldırılamaz (boş yine "hepsi" demek olurdu).
                      const isCashierPick = reset.person.role === 'cashier'
                      const shown = isCashierPick && picked.length === 0 ? halls.map((x) => x.id) : picked
                      const on = shown.includes(h.id)
                      const toggle = () => {
                        let next = on ? shown.filter((id) => id !== h.id) : [...shown, h.id]
                        if (isCashierPick) {
                          if (next.length === 0) return
                          if (next.length === halls.length) next = []
                        }
                        setReset({ ...reset, perms: { ...reset.perms, halls: next } })
                      }
                      return (
                        <div key={h.id} className={`fchip ${on ? 'is-on' : ''}`}>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={on}
                            className="fchip__toggle hallpick__toggle"
                            onClick={toggle}
                          >
                            <span
                              className="fchip__icon"
                              style={on ? undefined : ({ '--hall': hallColor(h.id, halls) } as CSSProperties)}
                            >
                              {on ? <CheckIcon /> : <i className="hallpick__dot" />}
                            </span>
                            {h.name}
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <p className="hallpick__hint">
                  {reset.person.role === 'cashier'
                    ? (reset.perms.halls ?? []).filter((id) => halls.some((h) => h.id === id)).length > 0
                      ? 'Kasiyer yalnız işaretli salonların masalarını görür.'
                      : 'Bütün salonlara bakar. Azaltmak için salonun işaretini kaldırın.'
                    : (reset.perms.halls ?? []).filter((id) => halls.some((h) => h.id === id)).length > 0
                      ? 'Garson yalnız seçili salonların masalarını görür.'
                      : 'Hiçbiri seçilmezse bütün salonlara bakar.'}
                </p>
              </div>
            )}

            {/* Yetkiler burada değil, kullanıcı kartının üstünde tek dokunuşla
                (kullanıcı, 13 Eki 2026). */}
            {reset.person.role === 'owner' && (
              <p className="dlg__hint">
                Şifrenizi giriş ekranındaki “Şifremi unuttum” ile
                yenileyebilirsiniz. Buradan yalnızca görünen ad düzenlenir.
              </p>
            )}

            {/* A PIN can only be replaced, never read back, so the new one has
                to be handed over in person. */}
            {reset.pin && (
              <p className="dlg__hint">
                Eski PIN geçersiz olur. Yeni PIN'i kişiye kendiniz iletin.
              </p>
            )}

            {staffError && <p className="dlg__error">{staffError}</p>}

            <div className="dlg__buttons">
              <button type="button" className="btn btn--ghost" onClick={() => setReset(null)}>
                Vazgeç
              </button>
              <button type="submit" className="btn btn--primary" disabled={staffBusy}>
                {staffBusy ? 'Değiştiriliyor…' : 'Değiştir'}
              </button>
            </div>
          </form>
        </div>
      )}

      {newStaff && (
        <div className="dlg" role="dialog" aria-modal="true">
          <form
            className="dlg__card"
            onSubmit={(e) => {
              e.preventDefault()
              void onCreateStaff()
            }}
          >
            <h2>Yeni Kullanıcı</h2>

            <label className="dlg__field">
              Görünen ad
              <input
                value={newStaff.displayName}
                onChange={(e) => setNewStaff({ ...newStaff, displayName: e.target.value })}
                placeholder="Ali Yılmaz"
              />
            </label>

            <label className="dlg__field">
              Kullanıcı adı (giriş için)
              <input
                value={newStaff.username}
                onChange={(e) => setNewStaff({ ...newStaff, username: e.target.value })}
                placeholder="ali"
                autoCapitalize="none"
                spellCheck={false}
                required
              />
            </label>

            <label className="dlg__field">
              PIN (4-8 rakam)
              <input
                value={newStaff.pin}
                onChange={(e) =>
                  setNewStaff({ ...newStaff, pin: e.target.value.replace(/\D/g, '').slice(0, 8) })
                }
                inputMode="numeric"
                placeholder="1234"
                required
              />
            </label>

            {/* "Rol" (20 Eki 2026): eskiden "Yetki" yazıyordu, yetkiler kartlardaki
                anahtarlar. Mutfak seçeneği eksikti; kod destekliyordu ama
                listede yoktu, mutfak personeli açılamıyordu. */}
            <label className="dlg__field">
              Rol
              <Select
                value={newStaff.role}
                onChange={(v) => setNewStaff({ ...newStaff, role: v as StaffDraft['role'] })}
                aria-label="Rol"
                options={[
                  // Müdürü yalnız patron açar (3 Kas 2026).
                  ...(viewerOwner
                    ? [{ value: 'manager', label: roleLabels.manager, hint: staffFull ? 'paketin sınırı doldu' : 'kasa + patronun seçtiği yetkiler' }]
                    : []),
                  { value: 'cashier', label: roleLabels.cashier, hint: staffFull ? 'paketin sınırı doldu' : 'kasa, ödeme alır' },
                  { value: 'waiter', label: roleLabels.waiter, hint: staffFull ? 'paketin sınırı doldu' : 'sipariş alır, ödeme almaz' },
                  // Mutfak hesabı Mutfak ekranı özelliğiyle (Plus paketi).
                  ...(hasFeature('mutfak')
                    ? [{ value: 'kitchen', label: roleLabels.kitchen, hint: 'mutfak ekranı, siparişleri hazırlar' }]
                    : []),
                ]}
              />
            </label>

            {newStaff.role === 'kitchen' && (
              <label className="dlg__field">
                Tezgahı
                <Select
                  value={newStaff.station}
                  onChange={(v) => setNewStaff({ ...newStaff, station: v })}
                  aria-label="Tezgahı"
                  options={stationOptions}
                />
              </label>
            )}

            {staffError && <p className="dlg__error">{staffError}</p>}

            <div className="dlg__buttons">
              <button type="button" className="btn btn--ghost" onClick={() => setNewStaff(null)}>
                Vazgeç
              </button>
              <button type="submit" className="btn btn--primary" disabled={staffBusy}>
                {staffBusy ? 'Ekleniyor…' : 'Ekle'}
              </button>
            </div>
          </form>
        </div>
      )}

      {ask && <ConfirmDialog {...ask} onClose={() => setAsk(null)} />}
      {prompt && <PromptDialog {...prompt} onClose={() => setPrompt(null)} />}
      {bulkTables && (
        <BulkTablesDialog
          hallName={halls.find((h) => h.id === openHall)?.name}
          onCancel={() => setBulkTables(false)}
          onConfirm={onAddManyTables}
        />
      )}
    </div>
  )
}
